import React from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';

import GoogleIcon from '../../MyComponents/icons/GoogleIcon';
import FacebookIcon from '../../MyComponents/icons/FacebookIcon';
import MicrosoftIcon from '../../MyComponents/icons/MicrosoftIcon';
import { socialSurface } from '../Home/HomeModals/loginButtonStyles';
import { TEXT_LINK } from '../Home/HomeDashboard/dashboardStyles';
import { startProviderLogin } from '../../common/utils';
import { GA_EVENT_TREE } from '../../gaConstants';
import { REDESIGN_COLORS, REDESIGN_FONTS } from '../../constants';

const useStyles = makeStyles(() => ({
    title: {
        margin: '0 0 10px',
        fontSize: '1.2rem',
        letterSpacing: '-0.02em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    text: {
        margin: '0 0 18px',
        color: REDESIGN_COLORS.body,
        fontSize: '0.95rem',
        lineHeight: 1.6,
    },
    row: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
    },
    button: {
        ...socialSurface,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        minHeight: '52px',
        padding: '0 20px 0 16px',
        fontSize: '0.95rem',
        '& svg': {
            width: '1.5rem',
            height: '1.5rem',
        },
    },
    foot: {
        margin: '18px 0 0',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        flexWrap: 'wrap',
    },
    link: TEXT_LINK,
    note: {
        margin: 0,
        color: REDESIGN_COLORS.muted,
        fontSize: '0.86rem',
        fontWeight: 600,
    },
}));

const PROVIDERS = [
    { target: '/google', labelId: 'home.loginGoogle', Icon: GoogleIcon, event: 'googleLogin' },
    { target: '/facebook', labelId: 'home.loginFacebook', Icon: FacebookIcon, event: 'facebookLogin' },
    { target: '/microsoft', labelId: 'home.loginMicrosoft', Icon: MicrosoftIcon, event: 'microsoftLogin' },
];

/**
 * Shown to a guest who tries to start a trial or a paid plan: guests cannot
 * subscribe, so instead of a dead end the card asks them to sign in with a
 * full account and offers the providers right there. `returnTo` is where the
 * OAuth round trip should land, normally the plan they were about to pick.
 */
const GuestSignInOptions = ({ title, text, returnTo, backTo, backLabelId }) => {
    const classes = useStyles();

    const signIn = (provider) => {
        if (returnTo) {
            // index.js replaces the location with this once the login completes
            window.localStorage.setItem('pathBeforeLogin', returnTo);
        }
        window.logAppActivity(GA_EVENT_TREE.mainPage.links[provider.event]);
        startProviderLogin(provider.target);
    };

    return (
        <div>
            <h2 className={classes.title}>{title || <FormattedMessage id="profile.guest.switch.title" />}</h2>
            <p className={classes.text}>{text || <FormattedMessage id="profile.guest.switch.text" />}</p>
            <div className={classes.row}>
                {PROVIDERS.map((provider) => (
                    <button
                        key={provider.target}
                        type="button"
                        onClick={() => signIn(provider)}
                        className={classes.button}
                    >
                        <provider.Icon />
                        <FormattedMessage id={provider.labelId} />
                    </button>
                ))}
            </div>
            <div className={classes.foot}>
                {backTo ? (
                    <Link to={backTo} className={classes.link}>
                        <FormattedMessage id={backLabelId || 'profile.guest.switch.notNow'} />
                    </Link>
                ) : null}
                <p className={classes.note}>
                    <FormattedMessage id="profile.guest.switch.note" />
                </p>
            </div>
        </div>
    );
};

export default GuestSignInOptions;
